import { ShoppingCart, Package, Heart, Star, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../lib/cartStore';
import { useAuthStore } from '../../lib/authStore';
import { addFavorite, removeFavorite } from '../../lib/api';
import { getPixPrice, getInstallments, formatPrice } from '../../lib/pricing';
import toast from 'react-hot-toast';
import CountdownTimer from './CountdownTimer';

export default function ProductCard({ product, isFavorite, onFavoriteToggle }) {
  const addItem = useCartStore((s) => s.addItem);
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.estoque <= 0) { toast.error('Produto sem estoque'); return; }
    const precoBase = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
    addItem({
      id: product.id, codigo: product.codigo, nome: product.nome,
      preco: precoBase,
      imagemUrl: product.imagemUrl, estoque: product.estoque,
    });
    toast.success('Adicionado ao carrinho!');
  };

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.estoque <= 0) { toast.error('Produto sem estoque'); return; }
    const precoBase = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
    addItem({
      id: product.id, codigo: product.codigo, nome: product.nome,
      preco: precoBase,
      imagemUrl: product.imagemUrl, estoque: product.estoque,
    });
    navigate('/carrinho');
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) { toast.error('Faça login para favoritar'); return; }
    try {
      if (isFavorite) {
        await removeFavorite(product.id);
        toast.success('Removido dos favoritos');
      } else {
        await addFavorite(product.id);
        toast.success('Adicionado aos favoritos!');
      }
      onFavoriteToggle?.(product.id);
    } catch (e) {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  return (
    <Link to={`/produto/${product.id}`} data-testid={`product-card-${product.id}`} className="h-full">
      <div className="group bg-zinc-900 border border-zinc-800/50 hover:border-red-600/50 transition-all duration-300 overflow-hidden relative hover:-translate-y-1 hover:shadow-xl hover:shadow-red-600/10 h-full flex flex-col">
        {product.isPromocao && (
          <div className="absolute top-2 right-2 z-10 bg-red-600 text-white text-[10px] font-bold px-2 py-1 uppercase -skew-x-6" data-testid={`promo-badge-${product.id}`}>
            Promoção
          </div>
        )}

        <button onClick={handleFavorite} data-testid={`favorite-btn-${product.id}`}
          className={`absolute top-2 left-2 z-10 p-1.5 rounded-full transition-all ${isFavorite ? 'bg-red-600 text-white' : 'bg-black/50 text-zinc-400 hover:text-red-500'}`}>
          <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>

        <div className="relative aspect-square bg-zinc-800 overflow-hidden flex-shrink-0">
          {product.imagemUrl ? (
            <img src={product.imagemUrl} alt={product.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Package className="h-16 w-16 text-zinc-700" /></div>
          )}
        </div>

        <div className="p-3 sm:p-4 flex flex-col flex-1">
          <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Cód: {product.codigo}</p>
          <h3 className="text-white font-semibold text-xs sm:text-sm mb-2 line-clamp-2 min-h-[2.5rem]">{product.nome}</h3>

          {product.reviewCount > 0 && (
            <div className="flex items-center gap-1.5 mb-2" data-testid={`rating-${product.id}`}>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`h-3 w-3 ${i <= Math.round(product.avgRating || 0) ? 'fill-yellow-500 text-yellow-500' : 'text-zinc-700'}`} />
                ))}
              </div>
              <span className="text-zinc-500 text-[10px]">({product.reviewCount})</span>
            </div>
          )}

          {product.isPromocao && product.promoExpiraEm && (
            <CountdownTimer expiresAt={product.promoExpiraEm} />
          )}

          <div className="mb-2 sm:mb-3">
            {(() => {
              const precoCartao = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
              const precoPix = getPixPrice(precoCartao);
              const parcelas = getInstallments(precoCartao);
              return (
                <div>
                  <p className="text-zinc-500 text-[11px] line-through" data-testid={`price-original-${product.id}`}>R$ {formatPrice(precoCartao)}</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-red-500 text-base sm:text-xl font-bold whitespace-nowrap" data-testid={`price-pix-${product.id}`}>R$ {formatPrice(precoPix)}</p>
                    <span className="bg-red-600 text-white text-[8px] sm:text-[9px] font-bold px-1 sm:px-1.5 py-0.5 uppercase rounded whitespace-nowrap">no pix</span>
                  </div>
                  {parcelas && (
                    <p className="text-zinc-500 text-[10px] sm:text-[11px] mt-0.5">ou {parcelas.parcelas}x de R$ {formatPrice(parcelas.valor)} sem juros</p>
                  )}
                </div>
              );
            })()}
          </div>

          <p className="text-[10px] text-zinc-500 mb-2 sm:mb-3">{product.estoque > 0 ? `${product.estoque} em estoque` : 'Sem estoque'}</p>

          <div className="mt-auto">
            <button onClick={handleBuyNow} disabled={product.estoque <= 0} data-testid={`buy-now-btn-${product.id}`}
              className="w-full bg-gradient-to-r from-red-600 to-red-400 hover:from-red-700 hover:to-red-500 disabled:bg-zinc-700 disabled:from-zinc-700 disabled:to-zinc-700 disabled:cursor-not-allowed text-white font-bold py-2 sm:py-2.5 text-xs sm:text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-red-600/20 whitespace-nowrap">
              <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span>{product.estoque > 0 ? 'Comprar Agora' : 'Indisponível'}</span>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
